/**
 * Admin-panel translation dictionary. Each entry's English copy doubles as the
 * inline fallback in `t(key, fallback)` — so missing translations don't blank out
 * the UI, they just render in English.
 *
 * Add new keys here as new pages land. Convention: dot.namespace.key.
 */

export type Dictionary = Record<string, string>;

const en: Dictionary = {};
// English uses inline fallbacks in components; the en map exists so the lookup chain works
// without special-casing. Keep it empty unless a key needs a different English variant.

const lv: Dictionary = {
  // ─── common ──────────────────────────────────────────────────────
  "common.loading": "Ielādē...",
  "common.save": "Saglabāt",
  "common.saving": "Saglabā...",
  "common.cancel": "Atcelt",
  "common.delete": "Dzēst",
  "common.edit": "Rediģēt",
  "common.create": "Izveidot",
  "common.creating": "Izveido...",
  "common.search": "Meklēt",
  "common.prev": "Iepriekšējā",
  "common.next": "Nākamā",

  // ─── languages / localization ────────────────────────────────────
  "languages.title": "Valodas",
  "languages.subtitle": "Ieslēdz vai izslēdz veikala valodas un skati, kas vēl nav iztulkots.",
  "languages.loadError": "Neizdevās ielādēt valodas",
  "languages.saved": "Valodas atjauninātas",
  "languages.savedHint": "Izmaiņas veikalā parādās minūtes laikā.",
  "languages.enabledTitle": "Ieslēgtās valodas",
  "languages.enabledHint":
    "Latviešu valoda vienmēr ir ieslēgta. Ieslēdz valodu, kad tās saturs ir iztulkots — ja ieslēgta tikai viena valoda, veikals valodas pārslēdzēju paslēpj.",
  "languages.baseLocale": "Pamata valoda — vienmēr ieslēgta",
  "languages.coverageTitle": "Tulkojumu pārklājums",
  "languages.coverageHint":
    "Nav iztulkots = ierakstam nav nosaukuma attiecīgajā valodā. Uzklikšķini uz skaitļa, lai redzētu, kuri.",
  "languages.colType": "Saturs",
  "languages.colTotal": "Kopā",
  "languages.colMissingEn": "Trūkst EN",
  "languages.colMissingRu": "Trūkst RU",
  "languages.products": "Produkti",
  "languages.categories": "Kategorijas",
  "languages.tags": "Pazīmes",
  "languages.grapes": "Vīnogu šķirnes",
  "languages.uiStrings": "Saskarnes teksti",
  "languages.uiStringsHint": "Uzskaitītās vārdnīcas atslēgas (iekļautie teksti gaida ārpuskodēšanu)",
  "languages.missingIn": "trūkst valodā",
  "languages.allTranslated": "Šeit viss ir iztulkots.",
  "languages.blog": "Blogs",
  "languages.editorTitle": "Tulkojumi",
  "languages.entries": "ieraksti",
  "languages.noEntries": "Nav ierakstu ar šiem filtriem.",
  "languages.stAll": "Visi",
  "languages.stMissingAny": "Trūkst kāda",
  "languages.stMissingEn": "Trūkst EN",
  "languages.stMissingRu": "Trūkst RU",
  "languages.stComplete": "Pabeigti",
  "common.clearFilters": "Notīrīt filtrus",
  "common.showing": "Rāda",
  "common.of": "no",
  "common.page": "Lapa",
  "common.deleteFailed": "Dzēšana neizdevās",
  "common.saveFailed": "Saglabāšana neizdevās",
  "common.notFound": "Nav atrasts",
  "common.required": "Obligāts",

  // ─── nav / sidebar ───────────────────────────────────────────────
  "nav.management": "Vadība",
  "nav.dashboard": "Pārskats",
  "nav.orders": "Pasūtījumi",
  "nav.ordersAll": "Visi pasūtījumi",
  "nav.ordersPending": "Gaidošie pasūtījumi",
  "nav.ordersAbandoned": "Pamesti grozi",
  "nav.catalog": "Katalogs",
  "nav.products": "Produkti",
  "nav.categories": "Kategorijas",
  "nav.grapeVarieties": "Vīnogu šķirnes",
  "nav.tags": "Pazīmes",
  "nav.banners": "Baneri",
  "nav.collections": "Kolekcijas",
  "nav.inventory": "Krājumi",
  "nav.customers": "Klienti",
  "nav.customerGroups": "Klientu grupas",
  "nav.marketing": "Mārketings",
  "nav.promotions": "Akcijas",
  "nav.content": "Saturs",
  "nav.pages": "Lapas",
  "nav.blog": "Blogs",
  "nav.settings": "Iestatījumi",
  "nav.translations": "Tulkojumi",
  "nav.languages": "Valodas",
  "nav.couriers": "Kurjeri",
  "nav.moneo": "Moneo ERP",
  "nav.catalogSettings": "Kataloga imports / eksports",
  "nav.storeSettings": "Veikala iestatījumi",
  "nav.team": "Komanda",
  "nav.performance": "Veiktspēja",
  "nav.performanceVisitors": "Apmeklētāji",
  "nav.performanceSearch": "Meklēšana",
  "nav.performanceIntegrations": "Integrācijas",
  "nav.communications": "Saziņa",
  "nav.signOut": "Iziet",
  "nav.brandSubtitle": "Admin",

  // ─── topnav ──────────────────────────────────────────────────────
  "topnav.liveVisitorsTooltip":
    "Aktīvie apmeklētāji veikalā (pēdējās 5 minūtes)",
  "topnav.integrationStatusTooltip":
    "Integrāciju veselība — pieslēgsies pēc heartbeat monitora",
  "topnav.integrationStatusHealthy": "Visas integrācijas darbojas",
  "topnav.integrationStatusDegraded":
    "Dažas integrācijas darbojas ar traucējumiem",
  "topnav.integrationStatusDown": "Integrācija nedarbojas",
  "topnav.integrationStatusUnknown": "Integrāciju statuss nezināms",
  "topnav.mentionsTitle": "Pieminējumi",
  "topnav.mentionsSubtitle": "Iekšējās piezīmes",
  "topnav.mentionsEmpty": "Nav nesenu pieminējumu",
  "topnav.ordersTitle": "Pasūtījumi",
  "topnav.ordersSubtitle": "Nesenā aktivitāte",
  "topnav.ordersEmpty": "Nav nesenu pasūtījumu",

  // ─── communications ──────────────────────────────────────────────
  "communications.title": "Saziņa",
  "communications.subtitle":
    "Visi transakciju un automatizācijas e-pasti, sūtīti caur Brevo.",
  "communications.refresh": "Atsvaidzināt",
  "communications.searchPlaceholder": "Meklēt pēc e-pasta vai temata…",
  "communications.allTemplates": "Visi veidi",
  "communications.allStatuses": "Visi statusi",
  "communications.allChannels": "Visi kanāli",
  "communications.totalCount": "Kopā {n}",
  "communications.loading": "Ielādē…",
  "communications.empty": "Nav atrastu sūtījumu.",
  "communications.colTemplate": "Veids",
  "communications.colRecipient": "Saņēmējs",
  "communications.colSubject": "Temats",
  "communications.colChannel": "Kanāls",
  "communications.colStatus": "Statuss",
  "communications.colLocale": "Valoda",
  "communications.colSent": "Nosūtīts",
  "communications.colOrder": "Pasūtījums",
  "communications.viewOrder": "Skatīt pasūtījumu",
  "communications.prev": "Iepriekšējā",
  "communications.next": "Nākamā",
  "communications.pageOf": "{p}. lapa no {n}",
  "orderDetail.communicationsTitle": "Saziņas vēsture",
  "orderDetail.communicationsEmpty": "Vēl nav nosūtītu ziņojumu.",

  // ─── integrations health page ────────────────────────────────────
  "integrations.title": "Integrācijas",
  "integrations.subtitle":
    "Visu ārējo servisu darbības stāvoklis. Atjaunina reizi 60 sekundēs.",
  "integrations.refresh": "Atsvaidzināt",
  "integrations.loading": "Ielādē…",
  "integrations.loadError": "Neizdevās ielādēt integrāciju veselību.",
  "integrations.rollupHealthy": "Visas integrācijas darbojas bez traucējumiem.",
  "integrations.rollupDegraded":
    "Vienai vai vairākām integrācijām ir periodiskas kļūdas.",
  "integrations.rollupDown":
    "Viena vai vairākas integrācijas nedarbojas — pārbaudi nekavējoties.",
  "integrations.colService": "Serviss",
  "integrations.colStatus": "Statuss",
  "integrations.colSuccessRate": "24h veiksmīgi",
  "integrations.colTotal24h": "24h pieprasījumi",
  "integrations.colErrors30m": "30m kļūdas",
  "integrations.colAvgMs": "Vidējais ms",
  "integrations.colLastSuccess": "Pēdējais veiksmīgais",
  "integrations.colLastError": "Pēdējā kļūda",
  "integrations.relSecondsAgo": "Pirms {n} sek.",
  "integrations.relMinutesAgo": "Pirms {n} min.",
  "integrations.relHoursAgo": "Pirms {n} st.",

  // ─── orders list ─────────────────────────────────────────────────
  "orders.title": "Pasūtījumi",
  "orders.pendingTitle": "Gaidošie pasūtījumi",
  "orders.searchPlaceholder": "Meklēt pēc pasūtījuma # vai klienta...",
  "orders.allStatuses": "Visi statusi",
  "orders.allPayments": "Visi maksājuma statusi",
  "orders.empty": "Pasūtījumi nav atrasti.",
  "orders.errorLoading": "Kļūda ielādējot pasūtījumus",
  "orders.colNumber": "Pasūtījums #",
  "orders.colCustomer": "Klients",
  "orders.colEmail": "E-pasts",
  "orders.colStatus": "Statuss",
  "orders.colPayment": "Maksājums",
  "orders.colFulfilment": "Piegāde",
  "orders.colTotal": "Kopā",
  "orders.colCreated": "Izveidots",
  "orders.statusPending": "Gaida",
  "orders.statusPaid": "Apmaksāts",
  "orders.statusReady": "Gatavs",
  "orders.statusFulfilled": "Izpildīts",
  "orders.statusCancelled": "Atcelts",
  "orders.statusRefunded": "Atmaksāts",
  "orders.payPending": "Gaida",
  "orders.payPaid": "Apmaksāts",
  "orders.payFailed": "Neizdevās",
  "orders.payPartiallyRefunded": "Daļēji atmaksāts",
  "orders.payRefunded": "Atmaksāts",
  "orders.fulfilmentPickup": "Pašizņemšana",
  "orders.fulfilmentCourier": "Kurjers",

  // ─── order detail ────────────────────────────────────────────────
  "orderDetail.title": "Pasūtījums",
  "orderDetail.notFound": "Pasūtījums nav atrasts",
  "orderDetail.items": "Preces",
  "orderDetail.subtotal": "Starpsumma",
  "orderDetail.discount": "Atlaide",
  "orderDetail.deposit": "Pudeles depozīts",
  "orderDetail.shipping": "Piegāde",
  "orderDetail.total": "Kopā",
  "orderDetail.depositLine": "iekļauts depozīts",
  "orderDetail.history": "Statusu vēsture",
  "orderDetail.historyEmpty": "Vēl nav statusu izmaiņu.",
  "orderDetail.statusChange": "Statuss",
  "orderDetail.paymentChange": "Maksājums",
  "orderDetail.payments": "Maksājumi",
  "orderDetail.paymentsEmpty": "Vēl nav reģistrētu maksājumu mēģinājumu.",
  "orderDetail.customer": "Klients",
  "orderDetail.b2bInvoice": "B2B rēķins",
  "orderDetail.legalInfo": "Uzņēmuma rekvizīti",
  "orderDetail.regNumber": "Reģ. nr.",
  "orderDetail.vatNumber": "PVN nr.",
  "orderDetail.legalAddress": "Juridiskā adrese",
  "orderDetail.giftWrap": "Dāvanu iesaiņojums",
  "orderDetail.giftWrapItems": "Iesaiņojamās preces",
  "storeSettings.title": "Veikala iestatījumi",
  "storeSettings.giftWrapTitle": "Dāvanu iesaiņojums",
  "storeSettings.giftWrapHint":
    "Cena par vienu iesaiņotu preci. Pircējs kasē izvēlas, kuras preces (un cik) iesaiņot.",
  "storeSettings.giftWrapPrice": "Cena par vienību (€)",
  "storeSettings.saved": "Iestatījumi saglabāti",
  "storeSettings.errorLoading": "Neizdevās ielādēt iestatījumus",
  "storeSettings.errorSaving": "Neizdevās saglabāt",
  "storeSettings.invalidPrice": "Nederīga cena",
  "orderDetail.fulfilment": "Piegāde",
  "orderDetail.chilled": "Atdzesēts",
  "orderDetail.banquet": "Banketam",
  "orderDetail.ageVerified": "Vecums apstiprināts",
  "orderDetail.notes": "Piezīmes",
  "orderDetail.customerNote": "No klienta",
  "orderDetail.internalNote": "Iekšējā",
  "orderDetail.internalNotePlaceholder":
    "Iekšēja piezīme (klients to neredzēs)",
  "orderDetail.markAsPaid": "Atzīmēt kā apmaksātu",
  "orderDetail.markAsReady": "Atzīmēt kā gatavu",
  "orderDetail.markAsFulfilled": "Atzīmēt kā izpildītu",
  "orderDetail.cancel": "Atcelt pasūtījumu",
  "orderDetail.refund": "Atmaksāt",
  "orderDetail.confirmCancelTitle": "Atcelt šo pasūtījumu?",
  "orderDetail.confirmCancelDesc":
    "Pasūtījums tiks atzīmēts kā atcelts. Krājumu piešķīrums tiek atgriezts. Darbība ir atgriezeniska, manuāli mainot statusu.",
  "orderDetail.confirmCancelAction": "Jā, atcelt pasūtījumu",
  "orderDetail.confirmRefundTitle": "Atzīmēt kā atmaksātu?",
  "orderDetail.confirmRefundDesc":
    "Pasūtījums un maksājuma statuss tiks pārslēgti uz “atmaksāts”. Tas neaktivizē reālu naudas atmaksu caur Montonio — tas notiks atsevišķi, kad integrācija būs gatava.",
  "orderDetail.confirmRefundAction": "Jā, atzīmēt kā atmaksātu",
  "orderDetail.updateFailed": "Atjaunināšana neizdevās",
  "orderDetail.noteSaveFailed": "Neizdevās saglabāt piezīmi",
  "orderDetail.noteSaved": "Piezīme saglabāta",

  // ─── abandoned carts ─────────────────────────────────────────────
  "carts.title": "Pamesti grozi",
  "carts.subtitle":
    "Grozi, kas netika pārvērsti par apmaksātu pasūtījumu. Atjaunošanas e-pasta automatizācija nāks vēlāk.",
  "carts.window1h": "Neaktīvs > 1h",
  "carts.window24h": "Neaktīvs > 24h",
  "carts.window7d": "Neaktīvs > 7d",
  "carts.window30d": "Neaktīvs > 30d",
  "carts.empty": "Šajā logā nav pamestu grozu.",
  "carts.errorLoading": "Kļūda ielādējot pamestos grozus",
  "carts.colCustomer": "Klients",
  "carts.colEmail": "E-pasts",
  "carts.colItems": "Preces",
  "carts.colSubtotal": "Starpsumma",
  "carts.colIdleFor": "Neaktīvs",
  "carts.colLastActivity": "Pēdējā aktivitāte",
  "carts.anonymous": "Anonīms",
  "carts.noEmail": "Nav e-pasta",

  // ─── customer detail ─────────────────────────────────────────────
  "customerDetail.notFound": "Klients nav atrasts",
  "customerDetail.customerSince": "Klients kopš",
  "customerDetail.profile": "Profils",
  "customerDetail.group": "Klientu grupa",
  "customerDetail.marketingConsent": "Mārketinga piekrišana",
  "customerDetail.skipAgeVerification": "Izlaist vecuma pārbaudi",
  "customerDetail.skipAgeVerificationHint":
    "Atbrīvot šo klientu no Dokobit (Smart-ID / Mobile-ID) vecuma pārbaudes pasūtījuma noformēšanā. Izmantot tikai tad, ja identitāte apstiprināta citādi.",
  "customerDetail.chilledDelivery": "Saņemt atdzesētu",
  "customerDetail.chilledDeliveryHint":
    "Rāda šim klientam kasē bezmaksas opciju “Saņemt atdzesētu” (6–8°C, termo iepakojumā). Pēc noklusējuma izslēgta.",
  "customerDetail.bankTransfer": "Pēcapmaksa ar bankas pārskaitījumu",
  "customerDetail.bankTransferHint":
    "Kasē pieejams maksājuma veids “Pēcapmaksa ar bankas pārskaitījumu” — pasūtījums tiek apstiprināts bez Montonio apmaksas un gaida manuālu apmaksas atzīmēšanu. Pēc noklusējuma izslēgta.",
  "customerDetail.business": "Uzņēmuma informācija",
  "customerDetail.companyName": "Uzņēmuma nosaukums",
  "customerDetail.companyRegNo": "Reģistrācijas Nr.",
  "customerDetail.vatNumber": "PVN reģ. Nr.",
  "customerDetail.recentOrders": "Nesenie pasūtījumi",
  "customerDetail.noOrders": "Vēl nav pasūtījumu.",
  "customerDetail.addresses": "Adreses",
  "customerDetail.noAddresses": "Nav saglabātu adrešu.",
  "customerDetail.defaultBilling": "Rēķini",
  "customerDetail.defaultShipping": "Piegāde",
  "customerDetail.meta": "Statistika",
  "customerDetail.lastLogin": "Pēdējā pieteikšanās",
  "customerDetail.totalOrders": "Pasūtījumu skaits",
  "customerDetail.totalSpend": "Kopējā summa",
  "customerDetail.emailRequired": "E-pasts ir obligāts",
  "customerDetail.saved": "Klients saglabāts",
  "customerDetail.moneoCustCode": "Moneo klienta kods",
  "customerDetail.moneoCustCodePlaceholder": "piem., 31245",
  "customerDetail.moneoCustCodeHint":
    "Ja norādīts, šī klienta pasūtījumu rēķini Moneo tiek grāmatoti ar šo klienta kodu. Ja tukšs — ar noklusējuma mazumtirdzniecības kodu no Moneo iestatījumiem. Kodam jau jāeksistē Moneo.",

  "common.saved": "Saglabāts",
  "promotions.title": "Akcijas",
  "promotions.subtitle":
    "Produkti ar aktīvu akcijas cenu. Tie parādās sadaļā Akcijas veikalā.",
  "promotions.empty": "Pagaidām nav akcijas produktu.",
  "promotions.add": "Pievienot akciju",
  "promotions.added": "Akcija pievienota",
  "promotions.remove": "Noņemt akciju",
  "promotions.searchPlaceholder": "Meklē produktu, ko likt akcijā…",
  "promotions.salePlaceholder": "Akcijas cena",
  "promotions.colProduct": "Produkts",
  "promotions.colBase": "Pamatcena",
  "promotions.colSale": "Akcijas cena",
  "promotions.colOff": "Atlaide",
  "pages.title": "Lapas",
  "pages.subtitle": "Satura lapas, ko veikals atver pēc slug.",
  "pages.new": "Jauna lapa",
  "pages.edit": "Rediģēt lapu",
  "pages.empty": "Vēl nav lapu.",
  "pages.active": "Aktīva",
  "pages.inactive": "Slēpta",
  "pages.activeHint": "Redzama veikalā",
  "pages.position": "Pozīcija",
  "pages.slugRequired": "Slug ir obligāts",
  "pages.colTitle": "Nosaukums",
  "pages.colSlug": "Slug",
  "pages.colActive": "Aktīva",
  "pages.fieldTitle": "Virsraksts",
  "pages.fieldMain": "Galvenā rindkopa",
  "pages.fieldLast": "Noslēguma rindkopa",
  "pages.fieldMetaTitle": "SEO virsraksts",
  "pages.fieldMetaDesc": "SEO apraksts",
  "pages.gallery": "Galerija",
  "pages.galleryHint":
    "Attēli tiek rādīti slaiderī zem lapas virsraksta (3 blakus uz datora, pa vienam mobilajā). Secība šeit = secība slaiderī.",
  "pages.galleryUpload": "Klikšķini vai ievelc attēlu, lai pievienotu",

  // ─── blog ────────────────────────────────────────────────────────
  "blog.title": "Blogs",
  "blog.subtitle": "Raksti, ko veikals atver sadaļā /blog.",
  "blog.new": "Jauns raksts",
  "blog.edit": "Rediģēt rakstu",
  "blog.empty": "Vēl nav rakstu.",
  "blog.untitled": "(bez nosaukuma)",
  "blog.errorLoading": "Kļūda ielādējot rakstus",
  "blog.confirmDelete": "Dzēst šo rakstu?",
  "blog.slugRequired": "Slug ir obligāts",
  "blog.statusDraft": "melnraksts",
  "blog.statusPublished": "publicēts",
  "blog.fieldSlug": "Slug",
  "blog.fieldPosition": "Pozīcija",
  "blog.fieldStatus": "Statuss",
  "blog.fieldPublishedAt": "Publicēšanas datums",
  "blog.fieldCover": "Vāka attēls",
  "blog.uploadHint": "Ievelc vāka attēlu šeit (vai klikšķini, lai izvēlētos)",
  "blog.fieldTitle": "Virsraksts",
  "blog.fieldExcerpt": "Kopsavilkums",
  "blog.fieldContent": "Saturs (HTML)",
  "blog.contentHint": "Neapstrādāts HTML — veikalā tiek attēlots tāds, kāds ir.",
  "blog.fieldMetaTitle": "SEO virsraksts",
  "blog.fieldMetaDesc": "SEO apraksts",


  // ─── customer groups ─────────────────────────────────────────────
  "customerGroups.title": "Klientu grupas",
  "customerGroups.subtitle":
    "Trīs pamata grupas (B2C + B2B-A + B2B-B), savienotas ar cenu sarakstu sistēmu. Kods ir tikai lasāms — to izmanto veikals un integrācijas.",
  "customerGroups.business": "B2B",
  "customerGroups.name": "Attēlojamais nosaukums",
  "customerGroups.priceList": "Cenu saraksts",
  "customerGroups.vatDisplay": "PVN attēlojums",
  "customerGroups.vatInc": "Iekļauts PVN",
  "customerGroups.vatExc": "Bez PVN",
  "customerGroups.saved": "Grupa saglabāta",

  // ─── team ────────────────────────────────────────────────────────
  "team.title": "Komanda",
  "team.subtitle": "Admin un personāla lietotāji ar pieeju šim panelim.",
  "team.invite": "Pievienot dalībnieku",
  "team.empty": "Vēl nav komandas dalībnieku.",
  "team.errorLoading": "Kļūda ielādējot komandu",
  "team.colEmail": "E-pasts",
  "team.colName": "Vārds",
  "team.colRole": "Loma",
  "team.colActive": "Aktīvs",
  "team.colLastLogin": "Pēdējā pieteikšanās",
  "team.colActions": "Darbības",
  "team.namePlaceholder": "Attēlojamais vārds",
  "team.roleAdmin": "Admin",
  "team.roleStaff": "Personāls",
  "team.statusActive": "Aktīvs",
  "team.statusDisabled": "Atspējots",
  "team.disable": "Atspējot",
  "team.enable": "Iespējot",
  "team.saved": "Komandas dalībnieks saglabāts",
  "team.deleted": "Dalībnieks noņemts",
  "team.created": "Dalībnieks pievienots",
  "team.createdHint":
    "Palaid `npm run admin:create -- --email <e-pasts> --password <pw>`, lai uzstādītu reālu paroli, līdz uzaicinājuma e-pasta plūsma ir gatava.",
  "team.createFailed": "Neizdevās pievienot dalībnieku",
  "team.passwordTooShort": "Parole ir pārāk īsa",
  "team.passwordMinLength": "Parolei jābūt vismaz 8 rakstzīmēm",
  "team.tempPassword": "Pagaidu parole",
  "team.tempPasswordPlaceholder": "Min. 8 rakstzīmes",
  "team.tempPasswordHint":
    "Līdz uzaicinājuma e-pasta plūsmai lietotājs paroli saņem manuāli. Pirmajā pieteikšanās reizē var to mainīt.",
  "team.confirmDeleteTitle": "Noņemt dalībnieku?",
  "team.confirmDeleteDesc":
    "Tiks noņemts dalībnieka pieejas konts. Audita ieraksti, ko viņš veidoja, paliek.",

  // ─── moneo settings ──────────────────────────────────────────────
  "moneoSettings.title": "Moneo ERP",
  "moneoSettings.subtitle":
    "Kad ieslēgts, katrs apmaksāts pasūtījums izveido priekšapmaksas rēķinu Moneo. Pēc noklusējuma izslēgts — ieslēdz, lai testētu ar partneri. API akreditācijas dati (MONEO_API_KEY / MONEO_COMPANY_ID) glabājas Railway env mainīgajos, nevis šeit.",
  "moneoSettings.statusOn": "Ieslēgts",
  "moneoSettings.statusOff": "Izslēgts",
  "moneoSettings.credentials": "Akreditācijas datu env mainīgie:",
  "moneoSettings.errorLoading": "Kļūda ielādējot Moneo iestatījumus",
  "moneoSettings.saved": "Moneo iestatījumi saglabāti",
  "moneoSettings.empty": "Nav atrasta Moneo iestatījumu rinda.",
  "moneoSettings.location": "Izpildes noliktava",
  "moneoSettings.pricelist": "Cenu lapa",
  "moneoSettings.custCode": "Mazumtirdzniecības klienta kods",
  "moneoSettings.hint":
    "Izveido priekšapmaksas rēķinu (createddatasource=2) katram apmaksātam pasūtījumam. Rindu preces izmanto katras preces SKU kā Moneo preces kodu.",

  // ─── couriers settings ───────────────────────────────────────────
  "couriersSettings.title": "Kurjeri",
  "couriersSettings.subtitle":
    "Pārslēdz katru kurjeru ieslēgt/izslēgt. Veikals piedāvā tikai aktīvos kurjerus pasūtījuma noformēšanā. API akreditācijas datu glabāšana — Railway env vars, nevis šeit.",
  "couriersSettings.statusOn": "Ieslēgts",
  "couriersSettings.statusOff": "Izslēgts",
  "couriersSettings.credentials": "Akreditācijas datu env mainīgie:",
  "couriersSettings.errorLoading": "Kļūda ielādējot kurjerus",
  "couriersSettings.saved": "Kurjers saglabāts",
  "couriersSettings.priceCourier": "Kurjera cena (€)",
  "couriersSettings.pricePakomats": "Pakomāta cena (€)",
  "couriersSettings.lockersEnabled": "Rādīt pakomātus grozā",
  "couriersSettings.lockersEnabledHint": "Ja izslēgts, āra pakomāti grozā nav pieejami. Venipak saņemšanas punkti veikalos paliek pieejami.",
  "couriersSettings.freeAbove": "Bezmaksas piegāde no (€)",
  "orders.allStores": "Visi izpildes veikali",
  "orderDetail.fulfilmentStore": "Izpildes veikals",
  "orderDetail.fulfilmentStorePlaceholder": "—",
  "orderDetail.earliestDelivery": "Agrākā piegāde (alkohols)",
  "orderDetail.earliestHandover": "Alkoholu izsniegt ne agrāk kā",
  "orderDetail.venipakStatus": "Venipak",
  "orderDetail.dispatchedAt": "Nosūtīts",
  "orderDetail.dispatch": "Sūtīt uz Venipak",
  "orderDetail.dispatching": "Sūta…",
  "orderDetail.dispatchOk": "Pasūtījums nosūtīts kurjeram",
  "orderDetail.dispatchAlready": "Jau nosūtīts",
  "orderDetail.dispatchFailed": "Sūtīšana neizdevās",
  "orderDetail.downloadLabel": "Lejupielādēt etiķeti (PDF)",
  "orderDetail.labelFailed": "Etiķetes lejupielāde neizdevās",
  "orderDetail.lastDispatchFailed": "Iepriekšējais sūtīšanas mēģinājums neizdevās. Spied vēlreiz, lai atkārtoti mēģinātu.",
  "orderDetail.pakomats": "Izvēlētais pakomāts",
  "orderDetail.bankTransfer": "Pēcapmaksa (pārskaitījums)",
  "translations.title": "Tulkojumi",
  "translations.subtitle": "Pārraksti veikala tekstus bez koda. Atstāj lauku tukšu, lai izmantotu LV noklusējumu zem tā.",
  "translations.searchPlaceholder": "Meklē pēc atslēgas vai teksta…",
  "translations.empty": "Nav atrasta neviena atslēga.",
  "translations.errorLoading": "Kļūda ielādējot tulkojumus",
  "translations.saved": "Saglabāts",

  // ─── auth ────────────────────────────────────────────────────────
  "auth.signInTitle": "Vīnoga Admin",
  "auth.signInPrompt": "Lai turpinātu, ielogojies.",
  "auth.email": "E-pasts",
  "auth.password": "Parole",
  "auth.signIn": "Ielogoties",
  "auth.signingIn": "Ielogojas...",
  "auth.invalidCredentials": "Nepareizs e-pasts vai parole",
  "auth.loginFailed": "Ielogošanās neizdevās",
  // Two-factor step.
  "auth.sendingCode": "Sūta kodu…",
  "auth.codePrompt": "Ievadiet kodu, ko nosūtījām uz",
  "auth.code": "Pieslēgšanās kods",
  "auth.verify": "Apstiprināt",
  "auth.verifying": "Pārbauda…",
  "auth.invalidCode": "Nepareizs kods",
  "auth.codeExpired": "Kodam beidzies derīguma termiņš. Pieprasiet jaunu.",
  "auth.tooManyAttempts":
    "Pārāk daudz nepareizu mēģinājumu. Pieprasiet jaunu kodu.",
  "auth.tooManyCodes": "Pārāk daudz mēģinājumu. Mēģiniet vēlāk.",
  "auth.codeSendFailed": "Neizdevās nosūtīt kodu. Mēģiniet vēlreiz.",
  "auth.codeResent": "Jauns kods nosūtīts",
  "auth.resend": "Nosūtīt jaunu kodu",
  "auth.resendIn": "Jaunu kodu pēc",
  "auth.back": "Atpakaļ",

  // ─── dashboard ───────────────────────────────────────────────────
  "dashboard.title": "Pārskats",
  "dashboard.subtitle":
    "Operatīvie rādītāji parādīsies šeit, tiklīdz pasūtījumi un integrācijas būs pieslēgtas.",
  "dashboard.todaysOrders": "Šodienas pasūtījumi",
  "dashboard.todaysOrdersHint": "Pieslēgsies, kad pasūtījumu API būs gatavs",
  "dashboard.stockAlerts": "Krājumu brīdinājumi",
  "dashboard.stockAlertsHint": "Cron darbs vēl jāpievieno",
  "dashboard.integrationHealth": "Integrāciju veselība",
  "dashboard.integrationHealthHint": "Heartbeat monitors vēl jāpievieno",

  // ─── products list ───────────────────────────────────────────────
  "products.title": "Produkti",
  "products.searchPlaceholder": "Meklēt pēc SKU vai slug...",
  "products.searchCategories": "Meklēt kategorijas...",
  "products.noCategoryFound": "Kategorija nav atrasta.",
  "products.allCategories": "Visas kategorijas",
  "products.allStatuses": "Visi statusi",
  "products.statusActive": "Aktīvs",
  "products.statusInactive": "Neaktīvs",
  "products.addProduct": "Pievienot produktu",
  "products.empty": "Produkti nav atrasti.",
  "products.errorLoading": "Kļūda ielādējot produktus",
  "products.confirmDelete": "Dzēst šo produktu?",
  "products.colImage": "Attēls",
  "products.colSku": "SKU",
  "products.colNameLv": "Nosaukums (LV)",
  "products.colCategory": "Kategorija",
  "products.colPrice": "Cena",
  "products.colStock": "Atlikums",
  "products.colCreated": "Izveidots",
  "products.colStatus": "Statuss",
  "products.colActions": "Darbības",
  "products.openInStore": "Atvērt internetveikalā",
  "products.openInStoreInactive":
    "Produkts nav aktīvs — internetveikalā tas vēl nav redzams",

  // ─── slug field (shared SlugInput) ───────────────────────────────
  "slug.invalidChars":
    "Atļauti tikai mazie burti bez garumzīmēm, cipari un defises.",
  "slug.tooLong": "Saite ir pārāk gara (maksimums 200 simboli).",
  "slug.taken": "Šī saite jau tiek izmantota citam ierakstam.",
  "slug.useSuggestion": "Izmantot",
  "slug.localeHint": "Neobligāti — ja tukšs, tiks izmantota pamata saite.",

  // ─── product editor ──────────────────────────────────────────────
  "productEditor.viewInStore": "Apskatīt produktu internetveikalā",
  "productEditor.viewInStoreInactive":
    "Produkts nav aktīvs — internetveikalā tas vēl nav redzams",
  "productEditor.newTitle": "Jauns produkts",
  "productEditor.editTitle": "Rediģēt produktu",
  "productEditor.save": "Saglabāt produktu",
  "productEditor.saving": "Saglabā...",
  "productEditor.missingFields": "Trūkst lauku",
  "productEditor.missingFieldsDesc":
    "SKU, slug un nosaukums latviešu valodā ir obligāti",
  "productEditor.created": "Izveidots",
  "productEditor.saved": "Saglabāts",
  "productEditor.savedDesc": "Produkts saglabāts veiksmīgi",
  "productEditor.notFound": "Produkts nav atrasts",
  "productEditor.tabLv": "Latviešu (noklusētā)",
  "productEditor.tabEn": "English",
  "productEditor.tabRu": "Русский",
  "productEditor.sectionGeneral": "Vispārīgā informācija",
  "productEditor.sectionSeo": "SEO",
  "productEditor.sectionBeverage": "Dzēriena metadati",
  "productEditor.sectionStatus": "Statuss un pieejamība",
  "productEditor.sectionLabels": "Piezīmes (birkas)",
  "productEditor.fieldMeasureType": "Mērvienība",
  "productEditor.measureVolume": "Tilpums",
  "productEditor.measureWeight": "Svars",
  "productEditor.fieldWeight": "Svars (g)",
  "productEditor.fieldMeasureDisplay": "Attēlot internetveikalā kā",
  "productEditor.measureAutoVolume": "Automātiski (cl / l)",
  "productEditor.measureAutoWeight": "Automātiski (g / kg)",
  "productEditor.unitPriceVolume": "Vienības cena: € / l",
  "productEditor.unitPriceWeight": "Vienības cena: € / kg",
  "productEditor.labelsHint":
    "Izvēlies no birkām, kas definētas sadaļā Birkas (/tags) — izvēlētās parādās kā uzlīmes uz produkta attēla internetveikalā (rādām ne vairāk kā 3).",
  "productEditor.noTags": "Nav nevienas aktīvas birkas. Izveido tās sadaļā Birkas.",
  "tags.colLogo": "Logo",
  "tags.uploadLogo": "Augšupielādēt",
  "common.uploading": "Augšupielādē…",
  "common.uploadFailed": "Augšupielāde neizdevās",
  "productEditor.stockManagedByMoneo":
    "Atlikumus pārvalda Moneo (POS darījumi + e-veikala pasūtījumi) — panelī tie nav rediģējami.",
  "productEditor.sectionIdentity": "Identitāte",
  "productEditor.sectionOrganization": "Organizēšana",
  "productEditor.sectionPricingInventory": "Cenas un krājumi",
  "productEditor.fieldName": "Nosaukums",
  "productEditor.fieldShortDesc": "Īsais apraksts",
  "productEditor.fieldShortDescHint": "(maks. 800 rakstzīmes)",
  "productEditor.fieldShortDescPlaceholder":
    "Īss produkta apraksts produkta lapas augšā...",
  "productEditor.fieldDescription": "Pilnais apraksts",
  "productEditor.fieldDescriptionPlaceholder": "Garais produkta apraksts...",
  "productEditor.fieldMetaTitle": "Meta nosaukums",
  "productEditor.fieldMetaTitlePlaceholder":
    "Lapas <title> meklētājprogrammām",
  "productEditor.fieldMetaDesc": "Meta apraksts",
  "productEditor.fieldMetaDescPlaceholder":
    "Meta apraksts meklētājprogrammām",
  "productEditor.fieldAbv": "Stiprums (%)",
  "productEditor.fieldVolume": "Tilpums (ml)",
  "productEditor.fieldVintage": "Raža",
  "productEditor.fieldCountry": "Valsts",
  "productEditor.fieldCountryPlaceholder": "Izvēlies valsti...",
  "productEditor.fieldCountrySearch": "Meklēt valsti...",
  "productEditor.fieldCountryEmpty": "Nav atbilstoša.",
  "productEditor.fieldRegion": "Reģions",
  "productEditor.fieldProducer": "Ražotājs",
  "productEditor.fieldBarcode": "Svītrkods (EAN)",
  "productEditor.fieldServeTemp": "Pasniegšanas temp. (°C)",
  "productEditor.fieldPdfUrl": "Tehniskā lapa (PDF)",
  "productEditor.fieldVideoUrl": "Video URL",
  "productEditor.sectionMedia": "Multimedija",
  "productEditor.fieldImage": "Produkta attēls",
  "productEditor.fieldImagePlaceholder": "Velc attēlu šeit",
  "productEditor.fieldStrengthScale": "Viegls ↔ Piesātināts (0–10)",
  "productEditor.fieldTypeScale": "Sauss ↔ Salds (0–10)",
  "productEditor.sectionBlocks": "Satura bloki",
  "productEditor.sectionBlocksHint":
    "Katrs aizpildītais bloks kļūst par atsevišķu cilni produkta lapā. Tukšie tiek paslēpti.",
  "productEditor.blockSmell": "Garšas piezīmes",
  "productEditor.blockTaste": "Garša",
  "productEditor.blockProducer": "Par ražotāju",
  "productEditor.blockGrapes": "Par vīnogām",
  "productEditor.blockStorage": "Glabāšana",
  "productEditor.blockPairing": "Ēdienu saderība",
  "productEditor.blockAwards": "Apbalvojumi",
  "productEditor.fieldSku": "SKU",
  "productEditor.fieldSlug": "Slug",
  "productEditor.fieldCategory": "Kategorija",
  "productEditor.fieldCategoryNone": "Nav",
  "productEditor.fieldCategories": "Kategorijas",
  "productEditor.searchCategories": "Meklēt kategorijas…",
  "productEditor.mainCategory": "Galvenā kategorija",
  "productEditor.expandAll": "Izvērst",
  "productEditor.collapseAll": "Sakļaut",
  "productEditor.inactiveCategory": "Neaktīva kategorija",
  "productEditor.toggleActive": "Aktīvs",
  "productEditor.toggleActiveHint": "Redzams veikalā, kad aktīvs",
  "productEditor.toggleFeatured": "Izcelts",
  "productEditor.toggleFeaturedHint": "Parādās izcelto produktu sadaļās",
  "productEditor.toggleIsAlcohol": "Ir alkohols",
  "productEditor.toggleIsAlcoholHint": "Ietekmē kategorijas izvietojumu",
  "productEditor.toggleAge": "Nepieciešama vecuma pārbaude",
  "productEditor.toggleAgeHint": "Aktivizē Dokobit pirms maksājuma",
  "productEditor.toggleDeposit": "Piemērojams depozīts",
  "productEditor.toggleDepositHint":
    "Pievieno €0,10 (bez PVN) par pudeli pasūtījuma summai",
  "productEditor.priceListLabel": "Cenu saraksts",
  "productEditor.priceListEmpty": "Cenu saraksti vēl nav konfigurēti.",
  "productEditor.salePrice": "Akcijas cena",
  "productEditor.salePricePlaceholder": "Nav atlaides",
  "productEditor.salePriceHint":
    "Atlaides cena no pamatcenas. Atstāj tukšu, ja nav akcijas. Jābūt zemākai par pamatcenu, lai stātos spēkā.",
  "productEditor.salePriceTooHigh":
    "Akcijas cena nav zemāka par pamatcenu — tā tiks ignorēta.",
  "productEditor.stockEmpty": "Krājumu atrašanās vietu vēl nav.",

  // ─── customers list ──────────────────────────────────────────────
  "customers.title": "Klienti",
  "customers.searchPlaceholder": "Meklēt klientus...",
  "customers.empty": "Klienti nav atrasti.",
  "customers.errorLoading": "Kļūda ielādējot klientus",
  "customers.colFirstName": "Vārds",
  "customers.colLastName": "Uzvārds",
  "customers.colEmail": "E-pasts",
  "customers.colPhone": "Telefons",
  "customers.colGroup": "Grupa",
  "customers.colLastLogin": "Pēdējā pieteikšanās",
  "customers.newCustomer": "Jauns klients",
  "customers.dialogFirstName": "Vārds",
  "customers.dialogLastName": "Uzvārds",
  "customers.dialogEmail": "E-pasts",
  "customers.dialogPhone": "Telefons",
  "customers.created": "Klients izveidots veiksmīgi.",
  "customers.errorCreating": "Kļūda izveidojot klientu",
  "customers.groupMissing": "Noklusētā grupa nav atrasta",
  "customers.groupMissingDesc":
    "Nevar atrast b2c klientu grupu. Iespējams, migrācijas nav iesētas.",
  "customers.todayLabel": "Šodien",
  "customers.yesterdayLabel": "Vakar",
  "customers.daysAgoSuffix": "d atpakaļ",

  // ─── inventory list ──────────────────────────────────────────────
  "inventory.title": "Krājumi",
  "inventory.subtitle":
    "Atrašanās vietas + krājumi pa atrašanās vietām. Redaktors un pārvietošana nāks vēlāk.",
  "inventory.locations": "Atrašanās vietas",
  "inventory.errorLoading": "Kļūda ielādējot",
  "inventory.colCode": "Kods",
  "inventory.colName": "Nosaukums",
  "inventory.colType": "Tips",
  "inventory.colPickup": "Pašizņemšanas punkts",
  "inventory.colStatus": "Statuss",
  "inventory.statusActive": "Aktīvs",
  "inventory.statusInactive": "Neaktīvs",
  "inventory.pickupYes": "jā",
  "inventory.typeShop": "veikals",
  "inventory.typeWarehouse": "noliktava",
  "inventory.note":
    "Krājumu redaktors par produktu atrodas produkta detaļās. Kustību vēsture un pārvietošanas saskarne nāks vēlāk.",

  // ─── coming soon placeholder ─────────────────────────────────────
  "comingSoon.subtitle":
    "Lapa rezervēta — pieslēgsies pēc kataloga slāņa pabeigšanas.",
  "comingSoon.heading": "Top",
  "comingSoon.description": "Šī lapa ir rezervēta admin izkārtojumā, bet vēl nav īstenota.",
  "comingSoon.note":
    "Šīs sadaļas shēma, API gala punkts un saskarne ir ieplānoti — skatīt PROJECT_PLAN.md punktu.",

  // ─── categories (editable) ───────────────────────────────────────
  "categories.title": "Kategorijas",
  "categories.subtitle":
    "Storefront navigācija veidojas no šī koka — augšējā līmeņa kategorijas ir nav elementi, to apakškategorijas aizpilda mega izvēlni. Reorder: rediģē 'Position' vērtību.",
  "categories.searchPlaceholder": "Meklēt pēc nosaukuma vai slug...",
  "categories.add": "Pievienot kategoriju",
  "categories.colNameLv": "Nosaukums (LV)",
  "categories.colSlug": "Slug",
  "categories.colUsedIn": "Produkti",
  "categories.colPosition": "Pozīcija",
  "categories.colStatus": "Statuss",
  "categories.statusActive": "aktīva",
  "categories.statusInactive": "neaktīva",
  "categories.empty": "Kategoriju vēl nav.",
  "categories.errorLoading": "Neizdevās ielādēt kategorijas",
  "categories.cantDelete": "Nevar dzēst",
  "categories.inUse":
    "Šī kategorija ir piesaistīta produktiem. Vispirms pārvieto produktus citur.",
  "categories.confirmDelete": "Dzēst šo kategoriju?",
  "categories.showInactive": "Rādīt neaktīvās",
  "categories.fieldNameLv": "Nosaukums (LV)",
  "categories.fieldNameEn": "Nosaukums (EN)",
  "categories.fieldNameRu": "Nosaukums (RU)",
  "categories.fieldSlug": "Slug",
  "categories.fieldParent": "Vecākā kategorija",
  "categories.fieldPosition": "Pozīcija",
  "categories.fieldColor": "Akcenta krāsa",
  "categories.fieldActive": "Aktīva",
  "categories.parentNone": "— (augšējais līmenis)",
  "categories.featuredTitle": "Mega-izvēlnes izceltais blociņš (pēc izvēles)",
  "categories.featuredEyebrow": "Mazais virsraksts",
  "categories.featuredHead": "Galvenais virsraksts",
  "categories.featuredCta": "Pogas teksts",
  "categories.featuredCtaHref": "Pogas saite",
  "categories.featuredImage": "Fona attēla URL",
  "categories.featuredBg": "Fona krāsa",
  "categories.featuredClear": "Notīrīt izcelto blociņu",
  "categories.newTitle": "Jauna kategorija",
  "categories.editTitle": "Rediģēt kategoriju",
  "categories.editorSubtitle":
    "Augšējā līmeņa kategorijas kļūst par veikala navigācijas vienumiem; apakškategorijas aizpilda mega-izvēlni.",
  "categories.create": "Izveidot kategoriju",
  "categories.nameRequired": "Nosaukums (LV) ir obligāts",
  "categories.slugRequired": "Slug ir obligāts",
  "categories.slugHint": "Veikala URL:",
  "categories.activeHint": "Redzama veikalā",
  "categories.sectionDetails": "Pamatinformācija",
  "categories.sectionImage": "Kategorijas attēls",
  "categories.uploadHint": "Nometiet kategorijas attēlu šeit (vai noklikšķiniet, lai izvēlētos)",
  "categories.imageHint":
    "Rādās kategoriju kartēs un mega-izvēlnes blociņā. Ja tukšs, izmanto izceltā blociņa attēlu.",
  "categories.showUrl": "Rādīt / ielīmēt neapstrādātu URL",

  // ─── tags ────────────────────────────────────────────────────────
  "tags.title": "Pazīmes",
  "tags.subtitle":
    "Šķērsgriezuma produkta pazīmes (BIO, Vegan, Naturāls, Jaunumi). Kategorijas veido navigācijas koku; pazīmes darbojas kā filtri pa virsu.",
  "tags.searchPlaceholder": "Meklēt pēc nosaukuma vai slug...",
  "tags.add": "Pievienot pazīmi",
  "tags.colSlug": "Slug",
  "tags.colNameLv": "Nosaukums (LV)",
  "tags.colColor": "Krāsa",
  "tags.colUsedIn": "Lietota produktos",
  "tags.colStatus": "Statuss",
  "tags.statusActive": "aktīva",
  "tags.statusInactive": "neaktīva",
  "tags.empty": "Pazīmju vēl nav.",
  "tags.newName": "Nosaukums (LV)",
  "tags.errorLoading": "Neizdevās ielādēt pazīmes",
  "tags.cantDelete": "Nevar dzēst",
  "tags.inUse":
    "Šī pazīme ir piesaistīta produktiem. Vispirms noņemiet pazīmi no produktiem.",
  "tags.confirmDelete": "Dzēst šo pazīmi?",

  // ─── banners ─────────────────────────────────────────────────────
  "banners.title": "Baneri",
  "banners.add": "Pievienot baneri",
  "banners.empty": "Baneru vēl nav.",
  "banners.errorLoading": "Neizdevās ielādēt banerus",
  "banners.confirmDelete": "Dzēst šo baneri?",
  "banners.dragHint": "Velc rindas, lai pārkārtotu. Saglabā automātiski.",
  "banners.savingOrder": "Saglabā pozīcijas…",
  "banners.statusActive": "aktīvs",
  "banners.statusInactive": "neaktīvs",
  "banners.orderFailed": "Neizdevās saglabāt pozīcijas",
  "banners.titleRequired": "Virsraksts ir obligāts",
  "banners.imageRequired": "Attēls ir obligāts",
  "banners.newTitle": "Jauns baneris",
  "banners.editTitle": "Rediģēt baneri",
  "banners.editorSubtitle": "Placement-vadīti promo bloki storefront pusē",
  "banners.create": "Izveidot baneri",
  "banners.sectionDetails": "Pamatinformācija",
  "banners.sectionLink": "Saite (pēc izvēles)",
  "banners.sectionAvailability": "Pieejamība",
  "banners.sectionImage": "Attēls",
  "banners.fieldTitle": "Virsraksts (iekšējais)",
  "banners.fieldAltText": "Alt teksts (pieejamībai un SEO)",
  "banners.altPlaceholder": "Apraksti attēlu",
  "banners.fieldPlacement": "Placement",
  "banners.fieldLinkType": "Saites tips",
  "banners.linkCategory": "Kategorija",
  "banners.linkCollection": "Kolekcija",
  "banners.linkCustom": "Pielāgots URL",
  "banners.pickCategory": "Izvēlies kategoriju",
  "banners.pickCollection": "Izvēlies kolekciju",
  "banners.selectOne": "izvēlies",
  "banners.fieldCustomUrl": "Pielāgots URL",
  "banners.resolvedUrl": "Saite:",
  "banners.fieldDateFrom": "Rādīt no",
  "banners.fieldDateTo": "Rādīt līdz",
  "banners.fieldActive": "Aktīvs",
  "banners.activeHint": "Rādīt storefront pusē",
  "banners.fieldDesktopSpan": "Desktop platums (grid kolonnas)",
  "banners.spanHint": "Mobilajā vienmēr aizpilda visu platumu, neatkarīgi no šī iestatījuma.",
  "banners.fieldImageUrl": "Attēla URL",

  // ─── collections ─────────────────────────────────────────────────
  "collections.title": "Kolekcijas",
  "collections.subtitle":
    "Atlasītu produktu komplekti ar pievilcīgu vāku. Piespraustās parādās sākumlapā kā attēls + 3×2 produktu kartīte.",
  "collections.add": "Pievienot kolekciju",
  "collections.empty": "Kolekciju vēl nav.",
  "collections.errorLoading": "Neizdevās ielādēt kolekcijas",
  "collections.confirmDelete": "Dzēst šo kolekciju? Produkti saglabājas, tikai kolekcija tiek noņemta.",
  "collections.statusActive": "aktīva",
  "collections.statusInactive": "neaktīva",
  "collections.pinHint": "Piespraust sākumlapā",
  "collections.newTitle": "Jauna kolekcija",
  "collections.editTitle": "Rediģēt kolekciju",
  "collections.editorSubtitle":
    "Atlasi produktus un vāku. Piespraustās kolekcijas redzamas sākumlapā.",
  "collections.create": "Izveidot kolekciju",
  "collections.sectionDetails": "Pamatinformācija",
  "collections.sectionProducts": "Produkti",
  "collections.sectionAvailability": "Pieejamība",
  "collections.sectionImage": "Vāka attēls",
  "collections.fieldName": "Nosaukums",
  "collections.fieldSlug": "Slug",
  "collections.slugHint": "Storefront URL:",
  "collections.fieldDescription": "Apraksts",
  "collections.descPlaceholder": "1–3 teikumi virs produktu režģa",
  "collections.searchProducts": "Meklē produktus pēc nosaukuma / SKU / slug…",
  "collections.noResults": "Nav atbilstošu.",
  "collections.emptyProducts": "Produktu vēl nav. Meklē augšā, lai pievienotu.",
  "collections.fieldDateFrom": "Rādīt no",
  "collections.fieldDateTo": "Rādīt līdz",
  "collections.fieldActive": "Aktīva",
  "collections.activeHint": "Redzama storefront pusē",
  "collections.fieldPinned": "Piespraust sākumlapā",
  "collections.pinnedHint": "Rāda sākumlapā kā attēls + 3×2 režģis",
  "collections.fieldImageUrl": "Attēla URL",
  "collections.imageHint":
    "Vertikāls (≈3:4) — tiek izmantots kā vertikāls panelis blakus produktu režģim.",
  "collections.fieldImageSide": "Attēla puse sākumlapas kartē",
  "collections.sideLeft": "Pa kreisi no režģa",
  "collections.sideRight": "Pa labi no režģa",
  "collections.nameRequired": "Nosaukums ir obligāts",
  "collections.slugRequired": "Slug ir obligāts",

  // ─── common (extras — `common.saving` lives above at top of file) ──
  "common.created": "Izveidots",

  // ─── language switcher ───────────────────────────────────────────
  "lang.switchLabel": "Valoda",
};

export const dictionaries: Record<"lv" | "en", Dictionary> = { en, lv };
